import React, { useState, useMemo } from 'react';
import { Download, Search, Filter, FileSpreadsheet } from 'lucide-react';
import { LOG_TYPES } from '../constants';
import { exportToCSV } from '../utils/helpers';

export default function OperationLogs({ logs, tasks }) {
  const [filters, setFilters] = useState({ type: '', user: '', search: '', dateFrom: '', dateTo: '' });
  const [showFilters, setShowFilters] = useState(false);

  const users = useMemo(() => {
    const set = new Set(logs.map(l => l.user).filter(Boolean));
    return Array.from(set);
  }, [logs]);

  const filtered = useMemo(() => {
    return logs.filter(l => {
      if (filters.type && l.type !== filters.type) return false;
      if (filters.user && l.user !== filters.user) return false;
      if (filters.search && !l.content?.toLowerCase().includes(filters.search.toLowerCase()) && !l.taskId?.toLowerCase().includes(filters.search.toLowerCase())) return false;
      if (filters.dateFrom && l.time && l.time < filters.dateFrom.replace(/-/g, '')) return false;
      if (filters.dateTo && l.time && l.time > filters.dateTo.replace(/-/g, '') + ' 23:59') return false;
      return true;
    });
  }, [logs, filters]);

  function handleExportCSV() {
    const rows = filtered.map(l => ({
      时间: l.time,
      操作人: l.user || '系统',
      类型: LOG_TYPES.find(t => t.key === l.type)?.label || l.type,
      任务编号: l.taskId || '-',
      任务标题: l.taskTitle || '-',
      内容: l.content,
    }));
    exportToCSV(rows, `操作日志_${new Date().toISOString().slice(0, 10)}.csv`);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-slate-800">操作日志</h1>
        <button onClick={handleExportCSV} className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg">
          <FileSpreadsheet className="w-4 h-4 mr-2" /> 导出CSV
        </button>
      </div>

      <div className="card p-4 mb-4">
        <div className="flex items-center space-x-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" placeholder="搜索日志内容或任务编号..." className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" value={filters.search} onChange={e => setFilters(prev => ({ ...prev, search: e.target.value }))} />
          </div>
          <button onClick={() => setShowFilters(!showFilters)} className={`inline-flex items-center px-3 py-2 text-sm border rounded-lg transition-colors ${showFilters ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
            <Filter className="w-4 h-4 mr-2" /> 筛选
          </button>
        </div>
        {showFilters && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 pt-4 border-t border-slate-100">
            <div>
              <label className="block text-xs text-slate-500 mb-1">操作类型</label>
              <select className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg bg-white" value={filters.type} onChange={e => setFilters(prev => ({ ...prev, type: e.target.value }))}>
                <option value="">全部</option>
                {LOG_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">操作人</label>
              <select className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg bg-white" value={filters.user} onChange={e => setFilters(prev => ({ ...prev, user: e.target.value }))}>
                <option value="">全部</option>
                {users.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">开始日期</label>
              <input type="date" className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg" value={filters.dateFrom} onChange={e => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))} />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">结束日期</label>
              <input type="date" className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg" value={filters.dateTo} onChange={e => setFilters(prev => ({ ...prev, dateTo: e.target.value }))} />
            </div>
          </div>
        )}
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 font-medium">
              <tr>
                <th className="px-4 py-3 text-left">时间</th>
                <th className="px-4 py-3 text-left">操作人</th>
                <th className="px-4 py-3 text-left">类型</th>
                <th className="px-4 py-3 text-left">任务编号</th>
                <th className="px-4 py-3 text-left">任务标题</th>
                <th className="px-4 py-3 text-left">内容</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">暂无日志</td></tr>}
              {filtered.map((l, i) => (
                <tr key={i} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{l.time}</td>
                  <td className="px-4 py-3 text-slate-700">{l.user || '系统'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${LOG_TYPES.find(t => t.key === l.type)?.color || 'bg-slate-100 text-slate-600'}`}>
                      {LOG_TYPES.find(t => t.key === l.type)?.label || l.type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-slate-500">{l.taskId || '-'}</td>
                  <td className="px-4 py-3 text-slate-600">{l.taskTitle || '-'}</td>
                  <td className="px-4 py-3 text-slate-700">{l.content}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
